import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Loader2, FileText, CheckCircle2, XCircle, Download,
  RefreshCw, ChevronDown, ChevronRight, ChevronsDownUp,
  Clock, Mail, Upload, UserCheck, UserX, Link2, Users, Check, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { formatCPF, unformatCPF } from '../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => s ? new Date(s).toLocaleString('pt-BR') : '-';

const STATUS_BADGE = {
  pendente: 'bg-slate-100 text-slate-700',
  enviado: 'bg-blue-100 text-blue-700',
  parcial: 'bg-amber-100 text-amber-700',
  finalizado: 'bg-emerald-100 text-emerald-700',
};

const GerenciarSolicitacoesPage = ({ module: propModule }) => {
  const location = useLocation();
  const activeModule = propModule || (location.pathname.includes('/juridico/') ? 'juridico' : 'rh');

  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const filterModule = activeModule;
  const [filterStatus, setFilterStatus] = useState('todos');
  const [expandedPersonKey, setExpandedPersonKey] = useState(null);
  const [expandedSolId, setExpandedSolId] = useState(null);

  // Create solicitation
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [createForm, setCreateForm] = useState({
    tipoDestinatario: 'email',
    email_destinatario: '',
    nome_destinatario: '',
    cpf_destinatario: '',
    funcionario_id: '',
    module: activeModule,
    tiposSelecionados: [],
  });
  const [creating, setCreating] = useState(false);

  // Funcionário auto-complete
  const [funcionarioOpen, setFuncionarioOpen] = useState(false);
  const [funcionarioSearch, setFuncionarioSearch] = useState('');

  // Approve/Reject
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectObs, setRejectObs] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { module: filterModule };
      if (filterStatus && filterStatus !== 'todos') params.status = filterStatus;
      const r = await axios.get(`${API}/api/documentos/solicitacoes`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSolicitacoes(r.data || []);
    } catch { toast.error('Erro ao carregar solicitações'); }
    finally { setLoading(false); }
  }, [filterModule, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const grupos = useMemo(() => {
    const map = {};
    for (const sol of solicitacoes) {
      const key = sol.funcionario_id || sol.cpf_destinatario;
      if (!map[key]) {
        map[key] = {
          key,
          nome: sol.nome_destinatario,
          cpf: sol.cpf_destinatario,
          email: sol.email_destinatario,
          funcionario_id: sol.funcionario_id,
          solicitacoes: [],
        };
      }
      map[key].solicitacoes.push(sol);
    }
    return Object.values(map).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [solicitacoes]);

  const statusSummary = (sols) => {
    const finalizados = sols.filter((s) => s.status === 'finalizado').length;
    const pendentes = sols.filter((s) => s.status !== 'finalizado').length;
    if (finalizados > 0 && pendentes === 0) return { label: 'Finalizado', className: 'bg-emerald-100 text-emerald-700' };
    if (pendentes > 0 && finalizados === 0) return { label: `${sols.length} pendente(s)`, className: 'bg-amber-100 text-amber-700' };
    return { label: `${finalizados}/${sols.length} finalizado(s)`, className: 'bg-blue-100 text-blue-700' };
  };

  useEffect(() => {
    if (!showCreateModal) return;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const tRes = await axios.get(`${API}/api/tipos-documentos?ativos=true`, { headers });
        setTiposDocumento(tRes.data || []);
      } catch { /* */ }
    })();
  }, [showCreateModal]);

  const searchFuncionarios = useCallback(async (q) => {
    if (!q || q.length < 2) { setFuncionarios([]); return; }
    try {
      const r = await axios.get(`${API}/api/employees`, {
        params: { search: q, active_only: true },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setFuncionarios(r.data || []);
    } catch { setFuncionarios([]); }
  }, []);

  useEffect(() => {
    if (funcionarioSearch.length < 2) { setFuncionarios([]); return; }
    const timer = setTimeout(() => searchFuncionarios(funcionarioSearch), 300);
    return () => clearTimeout(timer);
  }, [funcionarioSearch, searchFuncionarios]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createForm.tiposSelecionados.length === 0) {
      toast.error('Selecione pelo menos um tipo de documento');
      return;
    }
    if (!createForm.email_destinatario || !createForm.nome_destinatario || !createForm.cpf_destinatario) {
      toast.error('Preencha todos os dados do destinatário');
      return;
    }
    setCreating(true);
    try {
      const docs = tiposDocumento
        .filter((t) => createForm.tiposSelecionados.includes(t.id))
        .map((t) => ({ tipo_documento_id: t.id, tipo_documento_nome: t.nome, obrigatorio: true }));

      await axios.post(`${API}/api/documentos/${activeModule}/solicitacoes`, {
        email_destinatario: createForm.email_destinatario,
        nome_destinatario: createForm.nome_destinatario,
        cpf_destinatario: unformatCPF(createForm.cpf_destinatario),
        funcionario_id: createForm.funcionario_id || null,
        documentos_esperados: docs,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

      toast.success('Solicitação criada e e-mail enviado!');
      setShowCreateModal(false);
      setCreateForm({
        tipoDestinatario: 'email',
        email_destinatario: '',
        nome_destinatario: '',
        cpf_destinatario: '',
        funcionario_id: '',
        module: activeModule,
        tiposSelecionados: [],
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar solicitação');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectFuncionario = (id) => {
    const func = funcionarios.find((f) => f.id === id);
    if (func) {
      setCreateForm({
        ...createForm,
        funcionario_id: id,
        email_destinatario: func.email || '',
        nome_destinatario: func.nome || '',
        cpf_destinatario: formatCPF((func.cpf || '').replace(/\D/g, '')),
      });
    }
  };

  const handleToggleTipo = (id) => {
    setCreateForm({
      ...createForm,
      tiposSelecionados: createForm.tiposSelecionados.includes(id)
        ? createForm.tiposSelecionados.filter((t) => t !== id)
        : [...createForm.tiposSelecionados, id],
    });
  };

  const handleApprove = async (solId, docId) => {
    setActionLoading(docId);
    try {
      await axios.post(`${API}/api/documentos/solicitacoes/${solId}/documentos/${docId}/aprovar`,
        {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Documento aprovado!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectObs) {
      toast.error('Observação obrigatória');
      return;
    }
    setActionLoading(rejectDialog.docId);
    try {
      await axios.post(`${API}/api/documentos/solicitacoes/${rejectDialog.solId}/documentos/${rejectDialog.docId}/recusar`,
        { observacao: rejectObs },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Documento recusado. Novo link enviado por e-mail.');
      setRejectDialog(null);
      setRejectObs('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao recusar');
    } finally {
      setActionLoading(null);
    }
  };

  const hasPendingDocs = (sol) =>
    sol.documentos_enviados?.some((d) => d.status === 'enviado') || false;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="h-6 w-6 text-indigo-600" />
            Solicitações de Documentos
            <Badge variant="outline" className="ml-2 text-xs">
              {activeModule.toUpperCase()}
            </Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Crie solicitações e acompanhe o envio de documentos por terceiros
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Nova Solicitação
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-3">
          <div className="w-48">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger><SelectValue placeholder="Todos os status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="enviado">Enviado</SelectItem>
                <SelectItem value="parcial">Parcial</SelectItem>
                <SelectItem value="finalizado">Finalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Destinatário</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Módulo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Documentos</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-5 w-5 inline animate-spin" /></TableCell></TableRow>
              ) : grupos.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nenhuma solicitação encontrada</TableCell></TableRow>
              ) : grupos.map((grupo) => {
                const isPersonOpen = expandedPersonKey === grupo.key;
                const sols = grupo.solicitacoes;
                const totalEsperados = sols.reduce((a, s) => a + (s.documentos_esperados?.length || 0), 0);
                const totalEnviados = sols.reduce((a, s) => a + (s.documentos_enviados?.length || 0), 0);
                const stat = statusSummary(sols);
                const latestDate = sols.reduce((latest, s) => s.criado_em > latest ? s.criado_em : latest, sols[0]?.criado_em || '');
                return (
                  <React.Fragment key={grupo.key}>
                    <TableRow
                      className="cursor-pointer hover:bg-slate-50 bg-slate-50/50"
                      onClick={() => { setExpandedPersonKey(isPersonOpen ? null : grupo.key); setExpandedSolId(null); }}
                    >
                      <TableCell>
                        {isPersonOpen ? <ChevronsDownUp className="h-4 w-4 text-indigo-500" /> : <Users className="h-4 w-4 text-indigo-500" />}
                      </TableCell>
                      <TableCell className="font-medium">{grupo.nome}</TableCell>
                      <TableCell className="text-sm text-slate-500">{grupo.email}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{activeModule.toUpperCase()}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`${stat.className} text-xs`}>{stat.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{totalEnviados}/{totalEsperados}</TableCell>
                      <TableCell className="text-sm text-slate-500">{fmtDate(latestDate)}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs text-slate-400">{sols.length} solicitação(ões)</span>
                      </TableCell>
                    </TableRow>

                    {isPersonOpen && sols.map((sol) => (
                      <React.Fragment key={sol.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-slate-50 border-l-4 border-l-indigo-200"
                          onClick={() => setExpandedSolId(expandedSolId === sol.id ? null : sol.id)}
                        >
                          <TableCell>
                            {expandedSolId === sol.id ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                          </TableCell>
                          <TableCell className="text-sm pl-8 text-slate-600">
                            <span className="text-[10px] text-slate-400 mr-1">ID:</span>{sol.id.slice(0, 8)}...
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{sol.email_destinatario}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{sol.module?.toUpperCase()}</Badge></TableCell>
                          <TableCell>
                            <Badge className={`${STATUS_BADGE[sol.status] || 'bg-slate-100'} text-xs`}>
                              {sol.status === 'finalizado' ? 'Finalizado' :
                               sol.status === 'enviado' ? 'Enviado' :
                               sol.status === 'parcial' ? 'Parcial' : 'Pendente'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {sol.documentos_enviados?.length || 0}/{sol.documentos_esperados?.length || 0}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{fmtDate(sol.criado_em)}</TableCell>
                          <TableCell className="text-right">
                            {hasPendingDocs(sol) && (
                              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setExpandedSolId(expandedSolId === sol.id ? null : sol.id); }}>
                                Revisar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>

                        {expandedSolId === sol.id && (
                          <TableRow>
                            <TableCell colSpan={8} className="bg-slate-50 p-4 pl-12">
                              <div className="space-y-3">
                                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                                  <div><span className="text-slate-500">CPF:</span> <span className="font-mono">{formatCPF(sol.cpf_destinatario)}</span></div>
                                  <div><span className="text-slate-500">E-mail:</span> {sol.email_destinatario}</div>
                                  <div><span className="text-slate-500">Funcionário ID:</span> {sol.funcionario_id || '-'}</div>
                                </div>

                                <h4 className="text-sm font-medium text-slate-700 mb-2">Documentos Esperados</h4>
                                <div className="space-y-2">
                                  {sol.documentos_esperados?.map((de) => {
                                    const enviado = sol.documentos_enviados?.find((d) => d.tipo_documento_id === de.tipo_documento_id);
                                    return (
                                      <div key={de.tipo_documento_id} className="flex items-center justify-between bg-white border rounded-lg p-3">
                                        <div className="flex items-center gap-2">
                                          <FileText className="h-4 w-4 text-slate-400" />
                                          <span className="text-sm">{de.tipo_documento_nome}</span>
                                          {de.obrigatorio && <span className="text-[10px] text-red-500">*</span>}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {!enviado && <span className="text-xs text-slate-400">Aguardando</span>}
                                          {enviado?.status === 'enviado' && (
                                            <>
                                              <Badge className="bg-blue-100 text-blue-700 text-xs">Enviado</Badge>
                                              <Button size="sm" variant="ghost" onClick={async () => {
                                                try {
                                                  const r = await axios.get(`${API}/api/documentos/download/${enviado.documento_id}`, {
                                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                                    responseType: 'blob',
                                                  });
                                                  const url = URL.createObjectURL(new Blob([r.data]));
                                                  const a = document.createElement('a'); a.href = url; a.download = enviado.arquivo_nome; a.click();
                                                  URL.revokeObjectURL(url);
                                                } catch { toast.error('Erro ao baixar'); }
                                              }}>
                                                <Download className="h-4 w-4" />
                                              </Button>
                                              <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700"
                                                disabled={actionLoading === enviado.documento_id}
                                                onClick={() => handleApprove(sol.id, enviado.documento_id)}>
                                                {actionLoading === enviado.documento_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                              </Button>
                                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                                                onClick={() => setRejectDialog({ solId: sol.id, docId: enviado.documento_id })}>
                                                <XCircle className="h-4 w-4" />
                                              </Button>
                                            </>
                                          )}
                                          {enviado?.status === 'aprovado' && <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Aprovado</Badge>}
                                          {enviado?.status === 'recusado' && (
                                            <div className="flex items-center gap-2">
                                              <Badge className="bg-red-100 text-red-700 text-xs">Recusado</Badge>
                                              <span className="text-xs text-red-500">{enviado.observacao_recusa}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {sol.historico?.length > 0 && (
                                  <>
                                    <h4 className="text-sm font-medium text-slate-700 mb-2 mt-4">Histórico</h4>
                                    <div className="relative pl-6 space-y-3">
                                      {sol.historico.map((evt, idx) => {
                                        const iconMap = {
                                          criada: Mail,
                                          acessada: Upload,
                                          upload: Upload,
                                          aprovado: UserCheck,
                                          recusado: UserX,
                                          novo_token: Link2,
                                        };
                                        const colorMap = {
                                          criada: 'text-indigo-500',
                                          acessada: 'text-blue-500',
                                          upload: 'text-blue-500',
                                          aprovado: 'text-emerald-500',
                                          recusado: 'text-red-500',
                                          novo_token: 'text-amber-500',
                                        };
                                        const labelMap = {
                                          criada: 'Solicitação criada',
                                          acessada: 'Portal acessado',
                                          upload: 'Documento enviado',
                                          aprovado: 'Documento aprovado',
                                          recusado: 'Documento recusado',
                                          novo_token: 'Novo link gerado',
                                        };
                                        const Icon = iconMap[evt.tipo] || Clock;
                                        return (
                                          <div key={idx} className="relative flex items-start gap-3">
                                            <div className="absolute -left-6 top-0.5 flex flex-col items-center">
                                              <div className={`p-0.5 rounded-full bg-white ${colorMap[evt.tipo] || 'text-slate-400'}`}>
                                                <Icon className="h-3.5 w-3.5" />
                                              </div>
                                              {idx < sol.historico.length - 1 && (
                                                <div className="w-px h-full min-h-[24px] bg-slate-200 mt-1" />
                                              )}
                                            </div>
                                            <div className="pb-3">
                                              <p className="text-xs font-medium text-slate-700">{labelMap[evt.tipo] || evt.tipo}</p>
                                              <p className="text-[11px] text-slate-500 mt-0.5">{evt.descricao}</p>
                                              <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(evt.data)}</p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nova Solicitação de Documentos</DialogTitle>
            <DialogDescription>
              Selecione o destinatário e os documentos solicitados
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Tipo de Destinatário</Label>
              <Select value={createForm.tipoDestinatario}
                onValueChange={(v) => setCreateForm({ ...createForm, tipoDestinatario: v, funcionario_id: '', email_destinatario: '', nome_destinatario: '', cpf_destinatario: '' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">E-mail avulso (fora da base)</SelectItem>
                  <SelectItem value="funcionario">Funcionário da base</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {createForm.tipoDestinatario === 'funcionario' ? (
              <div>
                <Label>Funcionário</Label>
                <Popover open={funcionarioOpen} onOpenChange={setFuncionarioOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={funcionarioOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createForm.funcionario_id
                        ? funcionarios.find((f) => f.id === createForm.funcionario_id)?.nome || 'Selecionado'
                        : 'Selecione um funcionário...'}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full min-w-[400px] p-0">
                    <Command>
                      <CommandInput
                        placeholder="Buscar por nome ou CPF..."
                        value={funcionarioSearch}
                        onValueChange={setFuncionarioSearch}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {funcionarioSearch.length >= 2 ? 'Nenhum funcionário encontrado.' : 'Digite ao menos 2 caracteres para buscar.'}
                        </CommandEmpty>
                        <CommandGroup>
                          {funcionarios.map((f) => {
                            const cpf = f.cpf ? formatCPF(f.cpf) : '';
                            return (
                              <CommandItem
                                key={f.id}
                                value={`${f.nome} ${cpf}`}
                                onSelect={() => {
                                  handleSelectFuncionario(f.id);
                                  setFuncionarioOpen(false);
                                  setFuncionarioSearch('');
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${createForm.funcionario_id === f.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <span>{f.nome}</span>
                                {cpf && <span className="ml-2 text-xs text-slate-400">{cpf}</span>}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input value={createForm.nome_destinatario} onChange={(e) => setCreateForm({ ...createForm, nome_destinatario: e.target.value })} required />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input type="email" value={createForm.email_destinatario} onChange={(e) => setCreateForm({ ...createForm, email_destinatario: e.target.value })} required />
              </div>
              <div>
                <Label>CPF *</Label>
                <Input value={createForm.cpf_destinatario} onChange={(e) => setCreateForm({ ...createForm, cpf_destinatario: formatCPF(e.target.value.replace(/\D/g, '').slice(0, 11)) })} required />
              </div>
            </div>

            <div>
              <Label>Módulo</Label>
              <div className="mt-2">
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  {activeModule === 'rh' ? 'RH' : 'Jurídico'}
                </Badge>
              </div>
            </div>

            <div>
              <Label>Documentos Solicitados *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1 max-h-48 overflow-y-auto border rounded-lg p-2">
                {tiposDocumento.length === 0 && <p className="text-sm text-slate-400 col-span-2 p-2">Nenhum tipo de documento cadastrado</p>}
                {tiposDocumento.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={createForm.tiposSelecionados.includes(t.id)}
                      onChange={() => handleToggleTipo(t.id)} className="rounded" />
                    {t.nome}
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating}>
                {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Criando...</> : <><Plus className="h-4 w-4 mr-2" />Criar e Enviar E-mail</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recusar Documento</DialogTitle>
            <DialogDescription>Informe o motivo da recusa. Um novo link será enviado ao destinatário.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Observação *</Label>
            <textarea
              value={rejectObs}
              onChange={(e) => setRejectObs(e.target.value)}
              className="w-full mt-2 p-3 border rounded-lg text-sm min-h-[100px]"
              placeholder="Descreva o motivo da recusa..."
              required
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialog(null); setRejectObs(''); }}>Cancelar</Button>
            <Button variant="destructive" onClick={handleReject} disabled={!rejectObs || actionLoading === rejectDialog?.docId}>
              {actionLoading === rejectDialog?.docId ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
              Recusar e Enviar Novo Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GerenciarSolicitacoesPage;
